import { useSyncExternalStore } from 'react';
import { Linking, Platform } from 'react-native';
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import { supabase } from './supabase';
import { APK_URL } from './config';
import { brDateTime } from './date';
import { t } from './i18n';

// Dois tipos de atualização:
// 1. Automática (pela internet): melhorias de telas e funções chegam sozinhas. O app baixa em segundo
//    plano e aplica ao reabrir, ou na hora se você tocar em "Aplicar agora".
// 2. Versão nova do aplicativo (APK): quando muda algo nativo. O app avisa e abre o link para baixar.
//    A versão mais recente fica na tabela "app_versao" do Supabase.

export type EstadoAtualizacao = {
  ota: 'desligado' | 'procurando' | 'baixando' | 'pronta' | 'em-dia' | 'erro';
  erro?: string;
  apk: { versao: string; link: string; notas: string | null } | null; // versão nova do APK, se houver
  ultima: number | null;
};
let estado: EstadoAtualizacao = { ota: Updates.isEnabled ? 'em-dia' : 'desligado', apk: null, ultima: null };
const ouvintes = new Set<() => void>();
const set = (p: Partial<EstadoAtualizacao>) => { estado = { ...estado, ...p }; ouvintes.forEach(f => f()); };
export const useAtualizacao = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => estado);

/** Versão do pacote de atualização (o que foi publicado pelo 5-PUBLICAR): muda sem APK novo. */
export const versaoPacote = () => Constants.expoConfig?.version ?? '0.0.0';
/** 0.72 (texto 10 C2): a versão que a pessoa vê, "0.72" (a do app.json, só os dois primeiros números). */
export const versaoCurta = () => versaoPacote().split('.').slice(0, 2).join('.');
/** Versão do APK (ou do app do iPhone) instalado no aparelho. No navegador, a do pacote. */
export const versaoApp = (): string => {
  try { const v = (globalThis as any).expo?.modules?.ExpoApplication?.nativeApplicationVersion; if (v) return String(v); } catch { /* sem módulo */ }
  return versaoPacote();
};
/** Texto curto sobre o pacote em uso: "0.4.0 · atualização de 05/10/2026 14:20" */
export function descricaoVersao() {
  const v = versaoApp();
  if (!Updates.isEnabled || Updates.isEmbeddedLaunch || !Updates.createdAt) return v;
  // app instalado (APK) e pacote publicado podem ter números diferentes: mostra os dois
  const p = versaoPacote();
  if (p !== v) return t('app {v} · atualização {p} de {data}', { v, p, data: brDateTime(Updates.createdAt.toISOString()) });
  return t('{v} · atualização de {data}', { v, data: brDateTime(Updates.createdAt.toISOString()) });
}

const maior = (a: string, b: string) => {
  const x = a.split('.').map(Number), y = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) { if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) > (y[i] ?? 0); }
  return false;
};

/** App instalado pela Play Store ou pela App Store (0.54)? Esses recebem versões novas pela loja:
 *  nunca mostrar o aviso de baixar APK (a Google Play proíbe o app se atualizar fora dela). */
export const instaladoPelaLoja = () => Updates.channel === 'loja';   // builds de hoje (APK e TestFlight) não usam este canal

let rodando = false;
/** Procura as duas atualizações. Com "forcar", ignora o intervalo mínimo de 30 minutos. */
export async function procurarAtualizacoes(forcar = false) {
  if (rodando) return;
  if (!forcar && estado.ultima && Date.now() - estado.ultima < 30 * 60 * 1000) return;
  rodando = true;
  try {
    // versão do APK
    if (supabase && Platform.OS !== 'web' && !instaladoPelaLoja()) {
      // Android: a função "apk" confere na Expo o APK mais recente e atualiza a tabela (sem passo manual)
      if (Platform.OS === 'android') await fetch(APK_URL + '?json=1').catch(() => null);
      const { data } = await supabase.from('app_versao').select('versao, link, notas').eq('plataforma', Platform.OS === 'ios' ? 'ios' : 'android').maybeSingle();
      set({ apk: data && data.versao && maior(data.versao, versaoApp()) ? { versao: data.versao, link: data.link, notas: data.notas } : null });
    }
    // atualização automática
    if (Updates.isEnabled && estado.ota !== 'pronta') {
      set({ ota: 'procurando', erro: undefined });
      const r = await Updates.checkForUpdateAsync();
      if (r.isAvailable) {
        set({ ota: 'baixando' });
        const f = await Updates.fetchUpdateAsync();
        set({ ota: f.isNew ? 'pronta' : 'em-dia' });
      } else set({ ota: 'em-dia' });
    }
    set({ ultima: Date.now() });
  } catch (e: any) {
    set({ ota: Updates.isEnabled ? 'erro' : 'desligado', erro: String(e?.message ?? e) });
  } finally { rodando = false; }
}

export const aplicarAgora = () => Updates.reloadAsync().catch(() => {});
// Android: sempre pelo link fixo, que leva ao APK mais recente pronto na Expo
export const baixarApk = () => { if (instaladoPelaLoja()) return; if (Platform.OS === 'android') Linking.openURL(APK_URL); else if (estado.apk?.link) Linking.openURL(estado.apk.link); };
